import { supabase } from '../lib/supabase';

export const platformAdminService = {
  // Get all companies with statistics
  async getAllCompanies() {
    try {
      const { data: companies, error: companiesError } = await supabase?.from('companies')?.select(`
          *,
          user_profiles(count)
        `)?.order('created_at', { ascending: false });

      if (companiesError) {
        throw companiesError;
      }

      // Get user counts for each company
      const companiesWithStats = await Promise.all(
        companies?.map(async (company) => {
          const { count: userCount } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('company_id', company?.id);

          const { count: activeUserCount } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('company_id', company?.id)?.eq('is_active', true);

          return {
            ...company,
            user_count: userCount || 0,
            active_user_count: activeUserCount || 0
          };
        }) || []
      );

      return { data: companiesWithStats, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Get system-wide statistics
  async getSystemStats() {
    try {
      const { count: totalCompanies } = await supabase?.from('companies')?.select('*', { count: 'exact', head: true });

      const { count: totalUsers } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      const { count: activeUsers } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('is_active', true);

      const { count: activeCompanies } = await supabase?.from('companies')?.select('*', { count: 'exact', head: true })?.eq('status', 'active');

      return {
        data: {
          totalCompanies: totalCompanies || 0,
          totalUsers: totalUsers || 0,
          activeUsers: activeUsers || 0,
          activeCompanies: activeCompanies || 0
        },
        error: null
      };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Get all users across companies
  async getAllUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select(`
          *,
          companies(name, slug, status)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.companyId) {
        query = query?.eq('company_id', filters?.companyId);
      }
      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }
      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Update company status
  async updateCompanyStatus(companyId, status) {
    try {
      const { data, error } = await supabase?.from('companies')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', companyId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', userId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Delete company (and all related data)
  async deleteCompany(companyId) {
    try {
      // This will cascade delete user_profiles due to foreign key constraints
      const { error } = await supabase?.from('companies')?.delete()?.eq('id', companyId);

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Assign platform admin role to user
  async promoteUserToPlatformAdmin(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ 
          role: 'platform_admin',
          company_id: null, // Platform admins don't belong to any company
          updated_at: new Date()?.toISOString() 
        })?.eq('id', userId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Enhanced create user method using Edge Function
  async createUserForCompany(userData) {
    try {
      // Check admin status first
      const { data: adminCheck, error: adminError } = await supabase?.rpc('is_platform_admin_from_auth');

      if (adminError) {
        return { 
          data: null, 
          error: 'Unable to verify user permissions. Please ensure you are logged in and try again.' 
        };
      }

      if (!adminCheck) {
        return { 
          data: null, 
          error: 'Access denied. Only platform administrators can create users. Please contact your system administrator.' 
        };
      }

      // Get current session for authentication
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        return { data: null, error: 'Authentication required. Please log in again.' };
      }

      // Call Edge Function instead of admin API directly
      const { data, error } = await supabase?.functions?.invoke('create-user', {
        body: {
          email: userData?.email,
          password: userData?.password,
          full_name: userData?.fullName,
          role: userData?.role || 'company_user',
          company_id: userData?.companyId
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });

      if (error) {
        // Provide specific error messages
        if (error?.message?.includes('User already registered')) {
          return { data: null, error: 'A user with this email already exists' };
        }
        if (error?.message?.includes('Invalid email')) {
          return { data: null, error: 'Please provide a valid email address' };
        }
        if (error?.message?.includes('Password')) {
          return { data: null, error: 'Password does not meet requirements. Must be at least 6 characters.' };
        }
        return { data: null, error: error?.message || 'Failed to create user' };
      }

      return { data: data?.data, error: null };
    } catch (error) {
      console?.error('Create user error:', error);
      return { 
        data: null, 
        error: `Unexpected error: ${error?.message || 'Please try again or contact support'}` 
      };
    }
  },

  // Alternative: Send invitation instead of creating user directly
  async inviteUserToCompany(userData) {
    try {
      // Check admin status first
      const { data: adminCheck, error: adminError } = await supabase?.rpc('is_platform_admin_from_auth');
      if (adminError || !adminCheck) {
        return { 
          data: null, 
          error: 'Access denied. Only platform administrators can invite users.' 
        };
      }

      // Send invitation email (requires auth.users.invite_via_email RLS policy)
      const { data, error } = await supabase?.auth?.admin?.inviteUserByEmail(userData?.email, {
        data: {
          full_name: userData?.fullName,
          role: userData?.role || 'company_user',
          company_id: userData?.companyId
        }
      });

      if (error) {
        return { data: null, error: error?.message };
      }

      // Create pending user profile
      const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.insert([{
        id: data?.user?.id,
        email: userData?.email,
        full_name: userData?.fullName,
        role: userData?.role || 'company_user',
        company_id: userData?.companyId,
        is_active: false // Will be activated when user accepts invitation
      }])?.select()?.single();

      if (profileError) {
        return { data: null, error: profileError?.message };
      }

      return { 
        data: { 
          message: 'Invitation sent successfully', 
          user: profileData 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // FIXED: New method to check current user's platform admin status using database function
  async checkPlatformAdminStatus() {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) {
        return { isAdmin: false, error: 'Not authenticated' };
      }

      // Use database function instead of direct query to avoid RLS issues
      const { data: isAdmin, error: adminError } = await supabase?.rpc('is_platform_admin_from_auth');

      if (adminError) {
        return { isAdmin: false, error: 'Could not verify admin status' };
      }

      // If user is admin, we can safely get their profile info
      if (isAdmin) {
        try {
          const { data: profile } = await supabase?.from('user_profiles')
            ?.select('role, is_active, full_name')
            ?.eq('id', user?.user?.id)
            ?.single();
          
          return { 
            isAdmin: true,
            role: profile?.role || 'platform_admin',
            isActive: profile?.is_active ?? true,
            fullName: profile?.full_name || 'Platform Administrator',
            error: null 
          };
        } catch (profileError) {
          // Even if profile query fails, we know they're admin from the function
          return { 
            isAdmin: true,
            role: 'platform_admin',
            isActive: true,
            fullName: 'Platform Administrator',
            error: null 
          };
        }
      }

      return { 
        isAdmin: false,
        role: 'unknown',
        isActive: false,
        fullName: null,
        error: null 
      };
    } catch (error) {
      return { isAdmin: false, error: error?.message };
    }
  },

  // Bulk create users for a company
  async createBulkUsers(usersData) {
    try {
      const results = [];
      const errors = [];

      for (const userData of usersData) {
        const result = await this.createUserForCompany(userData);
        if (result?.error) {
          errors?.push({ email: userData?.email, error: result?.error });
        } else {
          results?.push(result?.data);
        }
      }

      return { 
        data: { 
          successful: results, 
          failed: errors,
          total: usersData?.length,
          successCount: results?.length,
          errorCount: errors?.length
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Delete user (and auth record)
  async deleteUser(userId) {
    try {
      // Delete user profile first
      const { error: profileError } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);
      
      if (profileError) throw profileError;

      // Delete auth user
      const { error: authError } = await supabase?.auth?.admin?.deleteUser(userId);
      
      if (authError) throw authError;

      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Reset user password
  async resetUserPassword(userId, newPassword) {
    try {
      const { error } = await supabase?.auth?.admin?.updateUserById(userId, {
        password: newPassword
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Get company details with user list
  async getCompanyDetails(companyId) {
    try {
      const { data: company, error: companyError } = await supabase?.from('companies')?.select('*')?.eq('id', companyId)?.single();

      if (companyError) {
        throw companyError;
      }

      const { data: users, error: usersError } = await supabase?.from('user_profiles')?.select('*')?.eq('company_id', companyId)?.order('created_at', { ascending: false });

      if (usersError) {
        throw usersError;
      }

      return { 
        data: { 
          ...company, 
          users: users || [] 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }
};